import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { UserPlusIcon, UserGroupIcon, UserIcon } from '@heroicons/react/24/outline';
import { contentApi, ContentType } from '@/services/contentApi';
import { useSubscription } from '@/hooks/useSubscription';
import AssignModal from './AssignModal';

interface AssignButtonProps {
  contentType: ContentType;
  contentId: string;
  className?: string;
}

/** Drop-in "who's this assigned to" + assign/reassign control, shared across every content type's detail and list views. Renders nothing when the org's plan doesn't include Teams/Assignment. */
const AssignButton: React.FC<AssignButtonProps> = ({ contentType, contentId, className }) => {
  const [showModal, setShowModal] = useState(false);
  const { data: subscription } = useSubscription();

  const { data: assignment } = useQuery({
    queryKey: ['content-assignment', contentType, contentId],
    queryFn: () => contentApi.getAssignment(contentType, contentId),
    enabled: subscription?.entitlements.approvalWorkflows === true,
  });

  if (subscription && subscription.entitlements.approvalWorkflows !== true) {
    return null;
  }

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        className={`inline-flex items-center gap-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600 px-2.5 py-1.5 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 ${className || ''}`}
      >
        {assignment?.assignee ? (
          <>
            {assignment.assigneeType === 'team' ? <UserGroupIcon className="h-4 w-4" /> : <UserIcon className="h-4 w-4" />}
            {assignment.assignee.name}
          </>
        ) : (
          <>
            <UserPlusIcon className="h-4 w-4" />
            Assign
          </>
        )}
      </button>

      {showModal && (
        <AssignModal contentType={contentType} contentId={contentId} onClose={() => setShowModal(false)} />
      )}
    </>
  );
};

export default AssignButton;
